import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import LoadingPlaceholder from "@/components/ui/loading-placeholder/LoadingPlaceholder";
import { MissionControlLoadingBar } from "@/components/mission-control/MissionControlLoading";
import { useSetWindowTitle } from "@/components/ui/os-layout";
import { useWindowLive } from "@/components/ui/os-layout/WindowActiveContext";
import type { MissionDetail as MissionDetailType } from "@/services/api/agentic-v2";
import { useAgenticSubscription } from "@/hooks/useAgenticSubscription";
import { useResolvedMissionEntities } from "@/hooks/useResolvedMissionEntities";
import { entityRefLabel } from "./entityRefs";
import { useCanAccessGuardians } from "@/hooks/useCanAccessGuardians";
import { useDeepLinkFocus } from "@/components/ui/deep-link-focus/useDeepLinkFocus";
import {
  WORKBENCH_ROOT_TEXT_CLASS,
  WORKBENCH_SURFACE_CLASS,
} from "@/components/ui/workbench";
import {
  createMissionDetailWsSubscription,
  useMissionDetail,
  useMissionDetailQueryClient,
} from "@/services/api/agentic-v2/queries/useMissionDetail";
import { MissionHeader } from "./detail/MissionHeader";
import { MissionStepLogDialog } from "./detail/MissionStepLogDialog";
import { SegmentedTabs } from "@/components/ui/segmented-tabs";
import { TimelineTab } from "./detail/TimelineTab";
import { ArtifactsTab } from "./detail/ArtifactsTab";
import { ChannelTab } from "./detail/ChannelTab";
import { GuardiansTab } from "./detail/GuardiansTab";
import { TaskThreadDrawer } from "./detail/TaskThreadDrawer";
import { useAppLocation } from "@/navigation/appLocation";
import {
  type MissionDetailTab,
  missionDetailCodec,
} from "@/navigation/apps/missionDetailLocation";

/**
 * Mission detail view (Phase 1, design-swept).
 *
 * Layout (from top to bottom of the main column):
 *   • Compact toolbar: entity chip · linked · attention · progress · agent dots
 *   • Stage rail (3-slot scrubber + dot track)
 *   • Segmented tab bar (Timeline / Artifacts / Channel)
 *   • Tab content
 *
 * The drawer slides in from the right when a task is clicked. The OS window
 * title bar provides the mission title — we deliberately don't repeat it
 * inside the content area.
 */
export interface MissionDetailProps {
  missionId: string;
}

export function MissionDetail({ missionId }: MissionDetailProps) {
  const canViewGuardians = useCanAccessGuardians();
  const isWindowLive = useWindowLive();
  const queryClient = useMissionDetailQueryClient();
  const [location, setLocation] = useAppLocation(missionDetailCodec);
  const [auditOpen, setAuditOpen] = useState(false);
  useDeepLinkFocus(location.focus);

  const activeTab = location.tab;
  const drawerTaskId = location.taskId ?? null;

  const missionQuery = useMissionDetail({ missionId });
  const mission = missionQuery.data?.mission ?? null;
  const agents = missionQuery.data?.agents ?? [];
  const loading = missionQuery.isLoading && !mission;
  const refreshing = missionQuery.isFetching && !loading;
  const error = missionQuery.error;

  const wsSubscription = useMemo(
    () => createMissionDetailWsSubscription(queryClient, missionId),
    [missionId, queryClient],
  );
  useAgenticSubscription(wsSubscription, { enabled: isWindowLive });

  const resolvedEntities = useResolvedMissionEntities(mission?.entities ?? []);
  const setWindowTitle = useSetWindowTitle();
  useEffect(() => {
    setWindowTitle(
      mission ? missionWindowTitle(mission, resolvedEntities) : "Mission",
    );
  }, [mission, resolvedEntities, setWindowTitle]);

  useEffect(() => {
    if (!canViewGuardians && location.tab === "guardians") {
      setLocation(
        location.focus ? { tab: "timeline", focus: undefined } : { tab: "timeline" },
      );
    }
  }, [canViewGuardians, location.focus, location.tab, setLocation]);

  if (loading && !mission) {
    return (
      <div className="flex h-full w-full flex-col gap-4 p-6">
        <LoadingPlaceholder height="3rem" width="100%" />
        <LoadingPlaceholder height="4.5rem" width="100%" />
        <LoadingPlaceholder height="min(28rem, 55vh)" width="100%" className="flex-1" />
      </div>
    );
  }

  if (error && !mission) {
    return (
      <div className="p-6 w-full text-slate-300">
        <FontAwesomeIcon
          icon={faExclamationTriangle}
          className="text-amber-500 mr-2"
        />
        Failed to load mission: {error?.message ?? "not found"}
      </div>
    );
  }

  if (!mission) {
    return (
      <div className="p-6 w-full space-y-4">
        <LoadingPlaceholder height="40px" />
        <LoadingPlaceholder height="60px" />
        <LoadingPlaceholder height="200px" />
      </div>
    );
  }

  const taskCount = mission.tasks.length;
  const reviewCount =
    mission.attention.review_task_count + mission.attention.blocked_task_count;

  return (
    <div
      className={clsx(
        "flex h-full w-full overflow-hidden p-4",
        WORKBENCH_ROOT_TEXT_CLASS,
        drawerTaskId !== null && "gap-4",
      )}
      data-testid={`mission-detail-${missionId}`}
    >
      <div
        className={clsx(
          "relative flex-1 flex flex-col min-w-0 overflow-hidden rounded-2xl transition-opacity duration-200",
          WORKBENCH_SURFACE_CLASS,
          refreshing && "pointer-events-none opacity-60",
          drawerTaskId !== null && "[contain:layout]",
        )}
      >
        <MissionControlLoadingBar active={refreshing} />
        <div className="px-6 pt-4 pb-4">
          <MissionHeader
            mission={mission}
            agents={agents}
            onOpenAudit={() => setAuditOpen(true)}
          />
        </div>

        <MissionStepLogDialog
          missionId={missionId}
          open={auditOpen}
          onClose={() => setAuditOpen(false)}
        />

        <div className="px-6 pt-2">
          <SegmentedTabs
            className="mt-2 -mb-px overflow-x-auto"
            showDivider={false}
            tabTestIdPrefix="mission-detail-tab"
            activeId={activeTab}
            onChange={(id) =>
              setLocation(
                location.focus
                  ? { tab: id as MissionDetailTab, focus: undefined }
                  : { tab: id as MissionDetailTab },
              )
            }
            tabs={[
              {
                id: "timeline",
                label: "Timeline",
                count: reviewCount > 0 ? reviewCount : undefined,
              },
              { id: "artifacts", label: "Artefacts" },
              ...(canViewGuardians
                ? [{ id: "guardians" as const, label: "Guardians" }]
                : []),
              { id: "channel", label: "Channel" },
            ]}
          />
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {activeTab === "timeline" && (
            <TimelineTab
              mission={mission}
              agents={agents}
              onTaskClick={(t) => {
                setLocation({ taskId: t._id });
              }}
            />
          )}
          {activeTab === "artifacts" && (
            <ArtifactsTab missionId={missionId} enabled />
          )}
          {canViewGuardians && activeTab === "guardians" && (
            <GuardiansTab missionId={missionId} />
          )}
          {activeTab === "channel" && (
            <ChannelTab missionId={missionId} enabled />
          )}
        </div>

        {taskCount === 0 && activeTab === "timeline" && (
          <div className="px-6 py-3 text-xs text-slate-500">
            No tasks have been queued for this mission yet.
          </div>
        )}
      </div>

      <TaskThreadDrawer
        taskId={drawerTaskId}
        open={drawerTaskId !== null}
        onClose={() => {
          setLocation({ taskId: undefined });
        }}
      />
    </div>
  );
}

function missionWindowTitle(
  mission: MissionDetailType,
  entities: MissionDetailType["entities"],
): string {
  const entity = entityRefLabel(entities[0]) || "Mission";
  const tmpl = mission.template_name || "";
  return tmpl ? `${entity} — ${tmpl}` : entity;
}
